<template>
  <Teleport to="body">
    <div v-if="open" class="fixed inset-0 z-[120] flex items-center justify-center">
      <div class="absolute inset-0 bg-black/60 backdrop-blur" @click="close"></div>
      <div class="relative w-full max-w-4xl bg-slate-950/95 border border-slate-700/60 rounded-2xl shadow-2xl overflow-hidden">
        <header class="flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-800">
          <div>
            <p class="text-xs uppercase tracking-[0.28em] text-slate-500">Bulk Upload</p>
            <h3 class="text-lg font-semibold text-white">Import Equipment Types & Serials</h3>
            <p class="text-slate-400 text-sm mt-1">
              Upload a CSV with make, model, descriptor, product, and serials. We will create new types as needed and skip duplicate serials.
            </p>
          </div>
          <button class="px-3 py-1 text-xs text-slate-400 hover:text-white" @click="close">Close</button>
        </header>

        <div class="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <section class="space-y-3">
            <div class="flex items-center justify-between">
              <label class="text-sm text-slate-300">Source CSV</label>
              <a
                href="/templates/equipment-bulk-upload-template.csv"
                download
                class="inline-flex items-center gap-2 text-xs font-medium text-primary-200 hover:text-primary-100 transition"
              >
                <span class="inline-flex h-5 w-5 items-center justify-center rounded-full border border-primary-400/50 bg-primary-500/20 text-primary-200">
                  ↓
                </span>
                Download template
              </a>
            </div>
            <input type="file" accept=".csv,.txt" class="input-dark w-full" @change="onFile" />
            <p class="text-xs text-slate-500">
              Required headers: <code>make, model, descriptor, product, serial, status</code>. Optional: <code>category</code>, <code>is_accessory</code>.
            </p>
          </section>

          <section v-if="rows.length" class="space-y-2">
            <div class="flex items-center justify-between">
              <h4 class="text-sm font-medium text-white">Preview ({{ rows.length }} rows)</h4>
              <button class="btn-secondary btn-secondary--xs" @click="rows = []">Clear</button>
            </div>
            <div class="border border-slate-800 rounded-lg overflow-hidden">
              <table class="min-w-full text-xs text-left text-slate-200">
                <thead class="bg-slate-900/60 text-slate-400 uppercase tracking-[0.18em]">
                  <tr>
                    <th class="px-3 py-2">Make</th>
                    <th class="px-3 py-2">Model</th>
                    <th class="px-3 py-2">Descriptor</th>
                    <th class="px-3 py-2">Product</th>
                    <th class="px-3 py-2">Serial</th>
                    <th class="px-3 py-2">Status</th>
                    <th class="px-3 py-2">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in rows"
                    :key="`${row.make}-${row.model}-${row.serial}-${idx}`"
                    :class="idx % 2 === 0 ? 'bg-slate-950/70' : 'bg-slate-950/40'"
                  >
                    <td class="px-3 py-2">{{ row.make }}</td>
                    <td class="px-3 py-2">{{ row.model }}</td>
                    <td class="px-3 py-2">{{ row.descriptor || '—' }}</td>
                    <td class="px-3 py-2">{{ row.product || '—' }}</td>
                    <td class="px-3 py-2">{{ row.serial }}</td>
                    <td class="px-3 py-2 uppercase">{{ row.status }}</td>
                    <td class="px-3 py-2 text-amber-300">{{ row.error || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section v-if="summary" class="text-sm text-slate-300 bg-slate-900/40 border border-slate-800 rounded-lg p-4 space-y-2">
            <div><strong>Upload summary:</strong></div>
            <ul class="list-disc list-inside text-xs text-slate-400 space-y-1">
              <li>{{ summary.createdTypes }} new equipment type(s) created</li>
              <li>{{ summary.existingTypes }} existing type(s) matched</li>
              <li>{{ summary.serialsInserted }} serial(s) inserted</li>
              <li>{{ summary.serialsSkipped }} serial(s) skipped (duplicates)</li>
            </ul>
          </section>

          <div v-if="serverError" class="text-rose-400 text-sm">{{ serverError }}</div>
        </div>

        <footer class="flex items-center justify-between px-6 py-4 border-t border-slate-800 bg-slate-950/90">
          <div class="text-xs text-slate-500">CSV size limit ~2MB (5k rows). Duplicate serials will be ignored.</div>
          <div class="flex items-center gap-2">
            <button class="btn-secondary" @click="close" :disabled="saving">Cancel</button>
            <button class="btn-primary" :disabled="saving || !rows.length" @click="submit">
              {{ saving ? 'Uploading…' : 'Import' }}
            </button>
          </div>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { ParseResult } from 'papaparse'
import Papa from 'papaparse'
import { useInventoryStore } from '@/stores/inventory'
import type { EquipmentStatus } from '@/types/inventory'
import type { BulkImportPayload } from '@/lib/inventoryApi'
import { bulkImportEquipment } from '@/lib/inventoryApi'
import { createLogger } from '@/lib/logger'

const log = createLogger('BulkUpload')

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ (e: 'close'): void; (e: 'imported'): void }>()

const inventory = useInventoryStore()
const rows = ref<CsvRow[]>([])
const saving = ref(false)
const summary = ref<UploadSummary | null>(null)
const serverError = ref('')

watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      await inventory.loadProducts()
    }
  },
)

const productNames = computed(() => inventory.products.map((p) => p.name))

const validStatuses: EquipmentStatus[] = ['available', 'reserved', 'checked-out', 'maintenance', 'retired', 'lost']

type CsvRow = {
  make: string
  model: string
  descriptor?: string
  product?: string
  status: EquipmentStatus
  serial: string
  isAccessory?: boolean
  category?: string
  error?: string
}

type UploadSummary = {
  createdTypes: number
  existingTypes: number
  serialsInserted: number
  serialsSkipped: number
}

const hasErrors = computed(() => rows.value.some((row) => !!row.error))

async function close() {
  rows.value = []
  summary.value = null
  serverError.value = ''
  emit('close')
}

async function onFile(event: Event) {
  const file = (event.target as HTMLInputElement)?.files?.[0]
  if (!file) return
  serverError.value = ''
  summary.value = null

  Papa.parse(file, {
    header: true,
    skipEmptyLines: true,
    complete: (results: ParseResult<Record<string, unknown>>) => {
      const parsed = results.data.map((row) => normalizeRow(row))
      rows.value = parsed
    },
    error: (error: Error) => {
      serverError.value = error.message
    },
  })
}

function normalizeRow(source: Record<string, unknown>): CsvRow {
  const make = String(source.make ?? source.Make ?? '').trim()
  const model = String(source.model ?? source.Model ?? '').trim()
  const descriptor = String(source.descriptor ?? source.Descriptor ?? '').trim() || undefined
  const product = String(source.product ?? source.Product ?? '').trim() || undefined
  const serial = String(source.serial ?? source.Serial ?? '').trim()
  const statusRaw = String(source.status ?? source.Status ?? 'available').trim().toLowerCase()
  const status = validStatuses.includes(statusRaw as EquipmentStatus)
    ? (statusRaw as EquipmentStatus)
    : 'available'
  const isAccessory = String(source.is_accessory ?? source.accessory ?? '').toLowerCase() === 'true'

  const row: CsvRow = {
    make,
    model,
    descriptor,
    product,
    serial,
    status,
    isAccessory,
  }

  if (!make || !model || !serial) {
    row.error = 'Missing make, model, or serial'
  }
  if (product && productNames.value.length && !productNames.value.includes(product)) {
    row.error = row.error ? `${row.error}; unknown product` : 'Unknown product'
  }
  return row
}

async function submit() {
  if (saving.value || !rows.value.length) return
  if (hasErrors.value) {
    serverError.value = 'Resolve validation issues before uploading.'
    return
  }
  saving.value = true
  serverError.value = ''
  summary.value = null

  const payload = rows.value.reduce<Record<string, BulkImportPayload>>((acc, row) => {
    const key = [row.make, row.model, row.descriptor ?? ''].join('::')
    const existing = acc[key]
    if (existing) {
      existing.serials.push(row.serial)
      return acc
    }
    acc[key] = {
      make: row.make,
      model: row.model,
      descriptor: row.descriptor,
      is_accessory: row.isAccessory ?? false,
      products: row.product ? [row.product] : undefined,
      serials: [row.serial],
      status: row.status,
    }
    return acc
  }, {})

  try {
    const result = await bulkImportEquipment(Object.values(payload))
    summary.value = result
    emit('imported')
  } catch (error: unknown) {
    log.error('Submit failed', error)
    serverError.value = error instanceof Error ? error.message : String(error)
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.input-dark {
  background-color: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(71, 85, 105, 0.6);
  color: #f1f5f9;
  font-size: 0.875rem;
  border-radius: 0.75rem;
  padding: 0.5rem 0.75rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.input-dark:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(100, 116, 139, 0.4);
  border-color: rgba(100, 116, 139, 0.65);
}

.btn-secondary {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(71, 85, 105, 0.75);
  color: rgba(203, 213, 225, 0.92);
  background: rgba(15, 23, 42, 0.68);
  transition: background-color 0.2s ease, color 0.2s ease;
}

.btn-secondary:hover {
  background: rgba(30, 41, 59, 0.85);
}

.btn-secondary--xs {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  border-radius: 0.5rem;
}

.btn-primary {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(59, 130, 246, 0.9);
  background: rgba(59, 130, 246, 0.85);
  color: #fff;
  transition: background-color 0.2s ease;
}

.btn-primary:hover {
  background: rgba(59, 130, 246, 1);
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
